import { atBuffer2DIndex } from 'features/dataLayers/geckoBinnedDataBuffer'
import { getMultipleCdfBinReadings } from 'features/dataLayers/load/getMultipleCdfBinReadings'
import isNil from 'lodash/isNil'
import { useMemo } from 'react'
import {
  contains,
  GeckoBinReading,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoComponentSpace,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  RawBinReading,
} from 'types'

import { useCdfRawValues } from './useCdfRawValues'

/**
 * An optimized helper hook which fetches CDF file data to determine a CDF
 * value at a given point (in component coordinates)
 * @param cdf             The component data frame to pull data from
 * @param coords          The coords to get CDF data at
 */
export const useCdfBinReading = (
  cdf: GeckoComponentDataFrame | undefined,
  coords: GeckoComponentCoords | undefined
): {
  value: GeckoMeasurement | undefined
  isLoading: boolean
} => {
  //----------------------------------------------------------------------------

  const { buffer: binnedDataBuffer, isLoading } = useCdfRawValues(cdf)

  const reading = useMemo(() => {
    if (!cdf || !coords || !binnedDataBuffer) return undefined

    const convertedCoords = coords.project(cdf.data.geometry.space)
    const translatedCoords = GeckoComponentCoords.CreateFromMeasurements(
      coords.componentID,
      convertedCoords.x.minus(cdf.data.geometry.start.x),
      convertedCoords.y.minus(cdf.data.geometry.start.y)
    )
    const u = translatedCoords.x.num / cdf.data.geometry.width.num
    const v = translatedCoords.y.num / cdf.data.geometry.height.num
    if (u < 0.0 || u >= 1.0 || v < 0.0 || v >= 1.0) return undefined // Out of bounds!

    const xIndex = Math.floor(u * cdf.data.binning.x_bin_count)
    const yIndex = Math.floor(v * cdf.data.binning.y_bin_count)
    const value = atBuffer2DIndex(binnedDataBuffer, xIndex, yIndex)
    if (value === undefined || Number.isNaN(value)) return undefined

    const measurementUnit = cdf.data.params.measurement_unit
    return {
      value: GeckoMeasurement.CreateFromValue(value, measurementUnit),
    }
  }, [binnedDataBuffer, coords, cdf])

  return useMemo(
    () => ({
      value: reading?.value,
      isLoading,
    }),
    [reading, isLoading]
  )
}

/**
 * An optimized helper hook which fetches CDF file data and returns an array of
 * CDF values along with their coordinates within provided bounds
 * @param cdf             The component data frame to pull data from
 * @param bounds          Optional bounds to limit search size to
 */
export const useMultipleCdfBinReadings = (
  cdf: GeckoComponentDataFrame | undefined,
  bounds?: GeckoComponentRect | undefined
): {
  readings: ReadonlyArray<GeckoBinReading> | undefined
  range: GeckoMeasurementRange | undefined
  mean: GeckoMeasurement | undefined
  isLoading: boolean
} => {
  //----------------------------------------------------------------------------

  const { buffer: binnedDataBuffer, isLoading } = useCdfRawValues(cdf)

  const data = useMemo(
    () =>
      getMultipleCdfBinReadings({
        cdf,
        bounds,
        binnedDataBuffer: binnedDataBuffer,
      }),
    [binnedDataBuffer, bounds, cdf]
  )

  return { ...data, isLoading }
}

/**
 * An optimized helper hook which fetches CDF file data and returns an array of
 * CDF values along with their coordinates within provided bounds
 * @param cdf             The component data frame to pull data from
 * @param bounds          Optional bounds to limit search size to
 */
export const useMultipleCdfBinReadingsRawValues = (
  cdf: GeckoComponentDataFrame | undefined,
  bounds?: GeckoComponentRect | undefined
): {
  readings: ReadonlyArray<RawBinReading> | undefined
  readingCoordinatesMeasurementSpace: GeckoComponentSpace | undefined
  readingValuesMeasurement: GeckoMeasurementUnit | undefined
  componentID: string | undefined
  range: GeckoMeasurementRange | undefined
  mean: GeckoMeasurement | undefined
  isLoading: boolean
} => {
  //----------------------------------------------------------------------------

  const { buffer: binnedDataBuffer, isLoading } = useCdfRawValues(cdf)

  const data = useMemo(() => {
    if (!cdf || !binnedDataBuffer || !bounds)
      return {
        readings: undefined,
        readingCoordinatesMeasurementSpace: undefined,
        readingValuesMeasurement: undefined,
        componentID: undefined,
        range: undefined,
        mean: undefined,
      }

    const measurementUnit = cdf.data.params.measurement_unit

    const cdfXStart = cdf.data.geometry.start.x.num
    const cdfYStart = cdf.data.geometry.start.y.num
    const cdfBinWidth = cdf.data.binning.bin_size.width.toNumber(
      cdf.data.geometry.x.unit
    )
    const cdfBinHeight = cdf.data.binning.bin_size.height.toNumber(
      cdf.data.geometry.y.unit
    )

    const projectedBounds = bounds.project(cdf.data.geometry.space)

    const boundsBinXStart =
      (projectedBounds.start.x.num - cdfXStart) / cdfBinWidth
    const boundsBinXEnd = (projectedBounds.end.x.num - cdfXStart) / cdfBinWidth
    const boundsBinYStart =
      (projectedBounds.start.y.num - cdfYStart) / cdfBinHeight
    const boundsBinYEnd = (projectedBounds.end.y.num - cdfYStart) / cdfBinHeight

    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    let sum = 0
    let count = 0
    const readings: RawBinReading[] = []

    for (let i = 0; i < binnedDataBuffer.values.length; i++) {
      const num = binnedDataBuffer.values.at(i)
      if (isNil(num) || Number.isNaN(num)) continue

      const binX = i % cdf.data.binning.x_bin_count
      const binY = Math.floor(i / cdf.data.binning.x_bin_count)
      const midBinX = binX + 0.5
      const midBinY = binY + 0.5

      if (
        !!bounds &&
        !contains(
          boundsBinXStart,
          boundsBinYStart,
          boundsBinXEnd,
          boundsBinYEnd,
          midBinX,
          midBinY
        )
      )
        continue

      readings.push({
        value: num,
        binRectXStart: binX * cdfBinWidth + cdfXStart,
        binRectYStart: binY * cdfBinHeight + cdfYStart,
        binRectXEnd: (binX + 1) * cdfBinWidth + cdfXStart,
        binRectYEnd: (binY + 1) * cdfBinHeight + cdfYStart,
        binCoordX: midBinX * cdfBinWidth + cdfXStart,
        binCoordY: midBinY * cdfBinHeight + cdfYStart,
      })

      min = Math.min(min, num)
      max = Math.max(max, num)
      sum += num
      count++
    }

    if (count === 0)
      return {
        readings: [],
        readingCoordinatesMeasurementSpace: cdf.data.geometry.space,
        readingValuesMeasurement: measurementUnit,
        componentID: cdf.data.geometry.componentID,
        range: undefined,
        mean: undefined,
      }

    return {
      readings: readings,
      readingCoordinatesMeasurementSpace: cdf.data.geometry.space,
      readingValuesMeasurement: measurementUnit,
      componentID: cdf.data.geometry.componentID,
      range: GeckoMeasurementRange.CreateFromValues(min, max, measurementUnit),
      mean: GeckoMeasurement.CreateFromValue(sum / count, measurementUnit),
    }
  }, [binnedDataBuffer, bounds, cdf])

  return { ...data, isLoading }
}

/**
 * An optimized helper hook which fetches CDF file data and returns an array of
 * CDF values along with their coordinates within the bounds of several provided
 * regions.
 * @param cdf             The component data frame to pull data from
 * @param regionBounds    An array of regions on this asset to pull data for
 */
export const useMultipleRegionsCdfBinReadings = (
  cdf: GeckoComponentDataFrame | undefined,
  regionBounds: GeckoComponentRect[] | undefined
): {
  regionValues: ReadonlyArray<{
    bounds: GeckoComponentRect
    readings: ReadonlyArray<GeckoBinReading>
    range: GeckoMeasurementRange | undefined
    mean: GeckoMeasurement | undefined
  }>
  isLoading: boolean
} => {
  //----------------------------------------------------------------------------

  const { buffer: binnedDataBuffer, isLoading } = useCdfRawValues(cdf)

  const regionValues = useMemo(() => {
    if (!cdf || !binnedDataBuffer || !regionBounds) return []

    const intermediateResults = regionBounds.map((boundsGroup) => ({
      bounds: boundsGroup,
      min: Number.POSITIVE_INFINITY,
      max: Number.NEGATIVE_INFINITY,
      sum: 0,
      count: 0,
      readings: new Array<GeckoBinReading>(),
    }))

    const componentID = cdf.tags.component
    const xUnit = cdf.data.geometry.x.unit
    const yUnit = cdf.data.geometry.y.unit
    const measurementUnit = cdf.data.params.measurement_unit

    const cdfXStart = cdf.data.geometry.start.x.num
    const cdfYStart = cdf.data.geometry.start.y.num
    const cdfBinWidth = cdf.data.binning.bin_size.width.convertTo(xUnit).num
    const cdfBinHeight = cdf.data.binning.bin_size.height.convertTo(yUnit).num

    for (let i = 0; i < binnedDataBuffer.values.length; i++) {
      const num = binnedDataBuffer.values.at(i)
      if (isNil(num) || Number.isNaN(num)) continue

      const binX = i % cdf.data.binning.x_bin_count
      const binY = Math.floor(i / cdf.data.binning.x_bin_count)
      const x0 = cdfXStart + binX * cdfBinWidth
      const y0 = cdfYStart + binY * cdfBinHeight
      const x1 = x0 + cdfBinWidth
      const y1 = y0 + cdfBinHeight

      const value = GeckoMeasurement.CreateFromValue(num, measurementUnit)
      const binRect = GeckoComponentRect.CreateFromXYs(
        componentID,
        x0,
        y0,
        x1,
        y1,
        xUnit,
        yUnit
      )
      const binCoords = GeckoComponentCoords.CreateFromMeasurements(
        componentID,
        binRect.x.end.minus(cdf.data.binning.bin_size.width.scale(0.5)),
        binRect.y.end.minus(cdf.data.binning.bin_size.height.scale(0.5))
      )

      intermediateResults.forEach((intermediateResult) => {
        if (GeckoComponentRect.Contains(intermediateResult.bounds, binCoords)) {
          intermediateResult.readings.push({ value, binCoords, binRect })
          intermediateResult.min = Math.min(intermediateResult.min, num)
          intermediateResult.max = Math.max(intermediateResult.max, num)
          intermediateResult.sum += num
          intermediateResult.count++
        }
      })
    }

    return intermediateResults.map(
      ({ bounds, readings, min, max, sum, count }) => ({
        bounds,
        readings,
        range:
          count === 0
            ? undefined
            : GeckoMeasurementRange.CreateFromValues(min, max, measurementUnit),
        mean:
          count === 0
            ? undefined
            : GeckoMeasurement.CreateFromValue(sum / count, measurementUnit),
      })
    )
  }, [binnedDataBuffer, regionBounds, cdf])

  return { regionValues, isLoading }
}
